import type {
  FilterKey,
  FilterProviderOptions,
  FilterSuggestion,
  OptionalKey,
  SuppliedFilterProviderOptions,
} from '@github-ui/filter'
import {FilterProviderType} from '@github-ui/filter'
import {defaultFilterProviderOptions, NOT_SHOWN} from '@github-ui/filter/constants/defaults'
import {TIME_RANGE_VALUES} from '@github-ui/filter/constants/dynamic-filter-values'
import {
  type IssueFieldSingleSelectOption,
  type IssueFieldType,
  KnownTypes,
} from '@github-ui/issue-fields-shared/issue-field'
import {type Icon, NoteIcon} from '@primer/octicons-react'

// Shared interfaces and types
export interface BaseIssueFieldData {
  name: string
  name_slug: string
  options?: IssueFieldSingleSelectOption[]
  data_type: IssueFieldType
  description?: string
}

// Shared constants
export const NUMERIC_FILTER_OPTIONS = {
  positive: true,
  negative: true,
  decimal: true,
}

type BaseSubFilterKey = OptionalKey<Omit<FilterKey, 'priority'>, 'icon'> & {
  type: FilterProviderType
  icon?: Icon
  values?: FilterSuggestion[]
}

type SelectSubFilterKey = BaseSubFilterKey & {
  type: 'select'
  values: FilterSuggestion[]
}

type FixedSubFilterKey = BaseSubFilterKey & {
  type: 'boolean' | 'text' | 'date' | 'number'
}

// Shared utility functions
export function buildFieldSubKey(
  fieldData: BaseIssueFieldData,
  options: SuppliedFilterProviderOptions,
  organizationName?: string,
): SelectSubFilterKey | FixedSubFilterKey {
  const {name, data_type, name_slug} = fieldData

  // Create display name and key with organization suffix if provided
  const displayName = organizationName ? `${name} (${organizationName})` : name
  const key = organizationName
    ? `${name_slug}-${organizationName}`
    : name_slug ?? name.toLowerCase().replace(/\s+/g, '-')

  // Build values based on data type
  let values: FilterSuggestion[] = []

  if (data_type === 'single_select') {
    values = (fieldData.options || []).map(option => ({
      value: option.name,
      displayName: option.name,
      priority: NOT_SHOWN,
    }))
  } else if (data_type === 'date') {
    values = TIME_RANGE_VALUES
  }

  // Get icon with fallback to prevent runtime errors
  const typeInfo = KnownTypes[data_type]
  const icon = typeInfo?.icon || NoteIcon // Fallback icon

  return {
    key,
    displayName,
    icon,
    type: getFieldType(data_type),
    values,
    options: getFilterOptions(options, data_type),
    aliases: [`field.${fieldData.name_slug || fieldData.name}`], // Always provide generic alias for suggestion matching
  }
}

export function getFilterOptions(options: SuppliedFilterProviderOptions, type: IssueFieldType): FilterProviderOptions {
  const baseOptions = {
    ...defaultFilterProviderOptions,
    filterTypes: {
      multiKey: false,
      multiValue: true,
      valueless: options.filterTypes?.valueless,
      hasValue: options.filterTypes?.hasValue,
    },
  }

  if (type === 'number') {
    return {
      ...baseOptions,
      ...NUMERIC_FILTER_OPTIONS,
    }
  }

  return baseOptions
}

export function getFieldType(data_type?: IssueFieldType): 'text' | 'select' | 'date' | 'number' {
  switch (data_type) {
    case 'text':
      return FilterProviderType.Text
    case 'single_select':
      return FilterProviderType.Select
    case 'date':
      return FilterProviderType.Date
    case 'number':
      return FilterProviderType.Number
    default:
      return FilterProviderType.Text
  }
}

export function mapNodeToFieldData(node: BaseIssueFieldData): BaseIssueFieldData {
  return {
    name: node?.name ?? '',
    name_slug: node?.name_slug,
    options: node?.options,
    data_type: node?.data_type,
    description: node?.description,
  }
}

export function createNestedFieldProviderConfig(
  description: string,
  subKeys: Array<SelectSubFilterKey | FixedSubFilterKey>,
) {
  return {
    key: 'field',
    displayName: 'Field',
    description,
    priority: 3,
    icon: NoteIcon,
    subKeys,
  }
}

// Generic response type for API calls
export type IssueFieldApiResponse = Record<string, unknown>

export async function fetchIssueFieldData(
  apiURL: string,
  abortController?: AbortController,
): Promise<IssueFieldApiResponse | null> {
  let response
  try {
    response = await fetch(apiURL, {
      method: 'GET',
      mode: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: abortController?.signal,
    })

    if (response?.ok) {
      return await response.json()
    }
    return null
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      throw error
    }
    return null
  }
}

// Helper function to extract organization name from repository scope
export function extractOrgFromRepoScope(repositoryScope: string): string {
  // Repository scope format is "owner/repo"
  const [owner] = repositoryScope.split('/')
  return owner || ''
}
